import Vue from 'vue';
import Router from 'vue-router';
import Editor from './../views/Editor.vue';
import Render from './../views/Render.vue';
import Wizard from './../views/Wizard.vue';
import Login from './../views/Login.vue';
import Logout from './../views/Logout.vue';
import store from './../store/store';
import { EventBus } from '@/helpers/event-bus';

Vue.use(Router);

const router = new Router({
    mode: 'history',
    routes: [
        {
            path: '/',
            redirect: '/editor'
        },
        {
            path: '/editor/:page?',
            name: 'editor',
            component: Editor,
            meta: {
                title: 'Editor | TECWORLD Homepage-Baukasten',
                requiresAuth: true,
                initLoad: true,
                snapshot: true
            }
        },
        {
            path: '/render/:page?',
            name: 'render',
            component: Render,
            meta: {
                requiresAuth: true,
                initLoad: true,
                snapshot: true
            }
        },
        {
            path: '/wizard/:page?',
            name: 'wizard',
            component: Wizard,
            meta: {
                title: 'Wizard | TECWORLD Homepage-Baukasten',
                requiresAuth: true,
                initLoad: true,
                snapshot: true
            }
        },
        {
            path: '/login',
            name: 'login',
            component: Login
        },
        {
            path: '/logout',
            name: 'logout',
            component: Logout,
            meta: { requiresAuth: true }
        },
        {
            path: '*',
            redirect: '/'
        }
    ]
});

// always remove queries from editor on first load
let redirectOnFirstLoad = false;
router.beforeEach((to, from, next) => {
    if (redirectOnFirstLoad === false) {
        redirectOnFirstLoad = true;
        if (to.name === 'editor') {
            next({
                path: '/editor'
            });
        } else {
            next();
        }
    } else {
        next();
    }
});

// redirect to login screen if not logged in
router.beforeEach((to, from, next) => {
    if (to.matched.some((record) => record.meta.requiresAuth)) {
        if (!Vue.prototype.$api.isLoggedIn()) {
            next({ path: '/login', query: { redirect: to.fullPath } });
        } else {
            next();
        }
    } else {
        next();
    }
});

// initial loading
router.beforeEach(async (to, from, next) => {
    if (to.meta.initLoad === true && Object.keys(store.state.tecpage).length === 0) {
        try {
            await store.dispatch('loadAllData');
            if (store.getters.isUserNotAllowed) {
                next({ path: '/logout', query: { redirect: to.fullPath } });
                return;
            }
            if (store.getters.shouldBeResetted) {
                await store.dispatch('resetData', 2);
            }
            await store.dispatch('checkForPackageChange');
            await store.dispatch('initHeadScripts');
            await store.dispatch('preloadFonts');
            await store.dispatch('updateHeadFavicon');
            await store.dispatch('updateHtmlClasses');
            next();
        } catch (e) {
            next({ path: '/login', query: { redirect: to.fullPath } });
        }
    } else {
        next();
    }
});

// force wizard
router.beforeEach((to, from, next) => {
    if (!['wizard', 'logout', 'login'].includes(to.name) && !store.getters.hasWizardFinished) {
        next({
            path: '/wizard'
        });
    } else {
        next();
    }
});

// prevent wizard
router.beforeEach((to, from, next) => {
    if (to.name === 'wizard' && store.getters.hasWizardFinished) {
        next({
            path: '/editor'
        });
    } else {
        next();
    }
});

// set title based on route
router.beforeEach((to, from, next) => {
    if (to.meta.title) {
        store.dispatch('updateHeadTitle', to.meta.title);
    }
    next();
});

// scroll to top if navigation to subpage
router.beforeEach((to, from, next) => {
    if (from.name === 'editor' && to.name === 'editor' && from.path !== to.path) {
        document.querySelector('.page-wrap').scrollTop = 0;
    }
    next();
});

// hide mobile menu
router.beforeEach((to, from, next) => {
    if (document.querySelector('.mainmenu__state:checked') !== null) {
        document.querySelector('.mainmenu__state:checked').checked = false;
    }
    next();
});

// take snapshots on every route change
router.beforeEach((to, from, next) => {
    if (
        to.meta.snapshot === true && // only take snapshots on predefined routes
        Object.keys(store.state.tecpage).length > 0 && // only take snapshots if store is filled
        store.getters.getSnapshotMetaChanging === false // prevent taking snapshots on undo/redo
    ) {
        store.dispatch('createSnapshot').then(() => {
            next();
        });
    } else {
        next();
    }
});

// if view is changed and unchanged fields are present, show close dialog
router.beforeEach((to, from, next) => {
    if (
        store.getters.differHasChangesInRoute(from.fullPath) &&
        // exclude nested routes
        to.fullPath.indexOf(from.fullPath) === -1 &&
        // exclude wizard
        !(from.name === 'wizard' && to.name === 'wizard')
    ) {
        EventBus.$emit('controlhead-close-open', from.fullPath);

        EventBus.$once('controlhead-close-saved', () => {
            next();
        });
        EventBus.$once('controlhead-close-discarded', () => {
            next();
        });
        EventBus.$once('controlhead-close-denied', () => {
            next(false);
        });
    } else {
        next();
    }
});

// prevent navigating to dead modules
router.beforeEach((to, from, next) => {
    if (to.query.module_id !== undefined && !store.getters.moduleExists(to.query.module_id)) {
        next({
            path: '/editor'
        });
    } else {
        next();
    }
});

export default router;
